import { BuildType } from '@/services/ApiService.ts';
import {
    OccupationModel,
    SimilarOccupationModel,
    OccupationTransitionModel,
    NextStepJobModel,
    LightcastNextJobCategory,
} from '@/apiTypes/OccupationTypes.ts';

export interface QueryOccupationFilters {
    name?: string;
    categoryIds?: string[];
    educationLevels?: number[];
    minSalary?: number;
    maxSalary?: number;
    nationalDemand?: number[];
    showLaunchpadOnly?: boolean;
    showFavorites?: boolean;
    showRecommended?: boolean;
    showTrending?: boolean;
}

export interface QueryOccupationParams {
    filters: QueryOccupationFilters;
    limit: number;
    page: number;
    skip?: boolean;
}

export type QueryOccupationsResponse = {
    rows: OccupationModel[];
    count: number;
};

export type RelatedOccupationsResponse = {
    similarOccupations: SimilarOccupationModel[];
    transitionOccupations: OccupationTransitionModel[];
};

export interface MsaOption {
    id: string;
    name: string;
}

export interface TrendingOccupation {
    lotOccupation: string;
    occupationName: string;
    trendinessScore: number;
    stateRank: number;
    snapshotDate: string;
    occupationIds: string[];
}

export const occupationApiEndpoints = (build: BuildType) => ({
    landingOccupations: build.query<OccupationModel[], void>({
        query: () => 'landing-occupations',
        transformResponse: (response: OccupationModel[]) => response,
    }),
    occupationDetails: build.query<OccupationModel, { id: string; stateId?: string; msaId?: string }>({
        query: ({ id, stateId, msaId }) => {
            const params = new URLSearchParams();
            if (stateId) params.set('stateId', stateId);
            if (msaId) params.set('msaId', msaId);
            const query = params.toString();
            return query ? `occupations/${id}?${query}` : `occupations/${id}`;
        },
    }),
    getMsasByOccupationAndState: build.query<MsaOption[], { id: string; stateAbbreviation: string }>({
        query: ({ id, stateAbbreviation }) =>
            `occupations/${id}/msas?stateAbbreviation=${encodeURIComponent(stateAbbreviation)}`,
        transformResponse: (response: MsaOption[]) => response,
    }),
    trendingOccupations: build.query<TrendingOccupation[], string>({
        query: stateId => ({
            url: `trending-occupations?stateId=${encodeURIComponent(stateId)}`,
            method: 'GET',
        }),
        transformResponse: (response: TrendingOccupation[]) => response,
    }),
    queryOccupations: build.query<QueryOccupationsResponse, QueryOccupationParams>({
        query: params => ({
            url: 'occupations/query',
            method: 'POST',
            body: params,
        }),
        transformResponse: (response: QueryOccupationsResponse) => response,
        providesTags: ['OccupationsQuery'],
    }),
    favoriteOccupation: build.mutation<void, string>({
        query: id => ({
            url: `user-occupations/${id}/favorite`,
            method: 'POST',
        }),
        invalidatesTags: ['Favorites', 'OccupationsQuery'],
    }),
    relatedOccupations: build.query<RelatedOccupationsResponse, string>({
        query: id => `occupations/${id}/related-occupations`,
    }),
    nextStepJobs: build.query<NextStepJobModel[], {
        id: string;
        categories: readonly LightcastNextJobCategory[];
        limit: number;
        scoreThreshold: number;
    }>({
        query: ({ id, categories, limit, scoreThreshold }) => (
            `occupations/${id}/next-step-jobs?limit=${limit}&scoreThreshold=${scoreThreshold}&categories=${categories.join(',')}`
        ),
    }),
});
